// hooks/useRedirectHandler.ts
import { navigate } from "@reach/router";
import { useCallback, useEffect } from "react";
import { useLocation } from "react-use";

import type { RedirectConfig } from "../types/RedirectConfig.type";
import {
	useCurrentCompany,
	useCurrentProfileType,
	useCurrentUser,
} from "hooks/auth";
import {
	useClientAccountPaths,
	useSupplierAccountPaths,
} from "newui/utils/navigation";
import { CompanyProfileType } from "../types/CompanyProfileType";
import { useReferralToken } from "./useReferralToken";
import { useGuestAccountPaths } from "newui/utils/navigation/navigationHelper";

export const REDIRECT_LOGIN_PATHS = [
	"/login",
	"/member/login",
	"/verify-email",
	"/",
	"/verification",
	"/guest/signup",
	"/two-factor",
	"/two-factor-setup",
];

export const useRedirectHandler = ({
	isVerified,
	role,
	companyId,
}: RedirectConfig): void => {
	const location = useLocation();

	const { referralToken } = useReferralToken();

	const redirectUrl = localStorage.getItem("redirect_path");
	const redirectProfile = localStorage.getItem("redirect_profile");
	const { currentUser, isGuest } = useCurrentUser();
	const currentCompany = useCurrentCompany();
	const isRfxFeatureEnabled = currentCompany?.enable_rfx_functionality;
	const clientPaths = useClientAccountPaths();
	const guestPaths = useGuestAccountPaths();
	const supplierPaths = useSupplierAccountPaths();
	const currentProfile = useCurrentProfileType();

	const clearRedirect = () => {
		localStorage.removeItem("redirect_path");
	};

	const navigateToRedirect = useCallback(() => {
		clearRedirect();
		navigate(redirectUrl);
	}, [redirectUrl]);

	// Save redirect params from URL
	useEffect(() => {
		const urlParams = new URLSearchParams(window.location.search);
		const redirect = urlParams.get("redirect");
		const page = urlParams.get("page");

		if (redirect) {
			localStorage.setItem("redirect_path", redirect);
			if (page) {
				localStorage.setItem(
					"redirect_profile",
					page.charAt(0).toUpperCase() + page.slice(1)
				);
			}
		}

		if (redirectUrl === location.origin + location.pathname) {
			clearRedirect();
		}
	}, [location.pathname, location.origin, redirectUrl, redirectProfile]);

	// handle guest redirects
	useEffect(() => {
		if (!isGuest) {
			return;
		}

		const url_path = window.location.pathname;

		if (REDIRECT_LOGIN_PATHS.includes(url_path)) {
			if (redirectUrl) {
				navigateToRedirect();
			} else {
				navigate(guestPaths?.home);
			}
			return;
		}

		if (redirectUrl) {
			navigateToRedirect();
			return;
		}
	}, [isGuest, guestPaths, redirectUrl, navigateToRedirect]);

	// Handle non-guest redirects after login
	useEffect(() => {
		if (isGuest || !currentUser) {
			return;
		}

		const url_path = window.location.pathname;

		// Shared token redirect
		if (referralToken && currentUser) {
			navigate(`/share-profile?sc=${referralToken.tokenValue}`);
			return;
		}

		// Verified user redirects
		if (isVerified && REDIRECT_LOGIN_PATHS.includes(url_path) && companyId) {
			if (redirectUrl) {
				navigateToRedirect();
				return;
			}

			navigate(
				currentProfile === CompanyProfileType.CLIENT
					? clientPaths.home
					: supplierPaths.home
			);
		}

		// Simple redirect if URL is set
		if (redirectUrl && !REDIRECT_LOGIN_PATHS.includes(url_path)) {
			navigate(redirectUrl);
		}
	}, [
		isVerified,
		redirectUrl,
		isGuest,
		role,
		companyId,
		isRfxFeatureEnabled,
		clientPaths,
		supplierPaths,
		currentProfile,
		currentUser,
		referralToken,
		navigateToRedirect,
	]);
};
